import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Checkbox,
  Button,
  CircularProgress,
  useTheme,
  styled,
  alpha,
  Collapse,
  Grid,
  Card,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Person,
  LocationOn,
  LocalShipping,
  Payment,
  Schedule,
  CheckCircle,
  Cancel,
  Refresh,
} from "@mui/icons-material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 20px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  whiteSpace: "nowrap",
  "&.header": {
    fontWeight: 600,
    fontSize: "0.875rem",
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    position: "sticky",
    top: 0,
    zIndex: 3,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  "& > .MuiTableCell-root": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}));

const ExpandableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  "& > .MuiTableCell-root": {
    borderBottom: "none",
  },
}));

const DetailCard = styled(Card)(({ theme }) => ({
  margin: "8px 0",
  padding: "20px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: "all 0.3s ease-in-out",
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusConfig = (status) => {
    // Ensure status is a string before calling toLowerCase
    const statusStr = status != null ? String(status).toLowerCase() : "";
    const configs = {
      pending: {
        bg: "#FFB74D",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
      confirmed: {
        bg: "#BA68C8",
        text: "#FFFFFF",
        icon: <CheckCircle sx={{ fontSize: "16px !important" }} />,
      },
      processing: {
        bg: "#BA68C8",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
      shipped: {
        bg: "#66BB6A",
        text: "#FFFFFF",
        icon: <LocalShipping sx={{ fontSize: "16px !important" }} />,
      },
      dispatch: {
        bg: "#66BB6A",
        text: "#FFFFFF",
        icon: <LocalShipping sx={{ fontSize: "16px !important" }} />,
      },
      delivered: {
        bg: "#66BB6A",
        text: "#FFFFFF",
        icon: <CheckCircle sx={{ fontSize: "16px !important" }} />,
      },
      cancelled: {
        bg: "#EF5350",
        text: "#FFFFFF",
        icon: <Cancel sx={{ fontSize: "16px !important" }} />,
      },
      returned: {
        bg: "#BA68C8",
        text: "#FFFFFF",
        icon: <Refresh sx={{ fontSize: "16px !important" }} />,
      },
      orders: {
        bg: "#FFB74D",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
    };
    return (
      configs[statusStr] || {
        bg: theme.palette.grey[400],
        text: theme.palette.common.white,
        icon: null,
      }
    );
  };

  const config = getStatusConfig(status);
  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: "capitalize",
    borderRadius: theme.shape.borderRadius,
    height: "auto",
    padding: "4px 10px",
    minWidth: "auto",
    "& .MuiChip-icon": {
      color: config.text,
      marginLeft: "4px",
      marginRight: "4px",
    },
    "& .MuiChip-label": {
      padding: "0 4px",
      fontSize: "0.875rem",
      color: config.text,
      fontWeight: 600,
    },
  };
});

const PaymentStatusChip = styled(Chip)(({ theme, status }) => {
  const getPaymentStatusConfig = (status) => {
    // Ensure status is a string before calling toLowerCase
    const statusStr = status != null ? String(status).toLowerCase() : "";
    const configs = {
      pending: {
        bg: "#fcc874",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
      outstanding: {
        bg: "#888cff",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
      paid: {
        bg: "#22d28a",
        text: "#FFFFFF",
        icon: <CheckCircle sx={{ fontSize: "16px !important" }} />,
      },
      failed: {
        bg: "#f4346e",
        text: "#FFFFFF",
        icon: <Cancel sx={{ fontSize: "16px !important" }} />,
      },
      refunded: {
        bg: "#888cff",
        text: "#FFFFFF",
        icon: <Refresh sx={{ fontSize: "16px !important" }} />,
      },
    };
    return (
      configs[statusStr] || {
        bg: theme.palette.grey[400],
        text: "#FFFFFF",
        icon: null,
      }
    );
  };

  const config = getPaymentStatusConfig(status);
  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: "capitalize",
    borderRadius: theme.shape.borderRadius,
    height: "auto",
    padding: "4px 10px",
    minWidth: "auto",
    "& .MuiChip-icon": {
      color: config.text,
      marginLeft: "4px",
      marginRight: "4px",
    },
    "& .MuiChip-label": {
      padding: "0 4px",
      fontSize: "0.875rem",
      color: config.text,
      fontWeight: 600,
    },
  };
});

const formatCurrency = (amount) => {
  if (amount == null || amount === "") return "—";
  return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
};

const getStatusDisplay = (order) => {
  const name = order.status?.name ?? order.order_status_display ?? "";
  let category = order.status?.category;
  if (!category && typeof order.status === "string") {
    category = order.status;
  }
  // Ensure category is a string
  const categoryStr = category != null ? String(category) : "";
  return { name: String(name || ""), category: categoryStr };
};

const getPaymentStatusDisplay = (order) => {
  const name = order.payment_status?.name ?? order.payment_status_display ?? "";
  let category = order.payment_status?.category;
  if (!category && typeof order.payment_status === "string") {
    category = order.payment_status;
  }
  // Ensure category is a string
  const categoryStr = category != null ? String(category) : "";
  return { name: String(name || ""), category: categoryStr };
};

/** Returns 'approved' | 'rejected' | null (pending) from order's request action/status field */
const getRequestAction = (order, field = "cancel_request_action") => {
  const raw = order[field] ?? order.request_action ?? order.request_status ?? order.status;
  if (raw == null || raw === "") return null;
  const s = String(raw).toLowerCase().trim();
  if (s === "approved" || s === "accept") return "approved";
  if (s === "rejected" || s === "reject") return "rejected";
  return null; // pending or any other value
};

const getStatusIcon = (status) => {
  let category;
  if (typeof status === "object" && status !== null && "category" in status) {
    category = status.category;
  } else if (typeof status === "object" && status !== null && "name" in status) {
    category = status.name;
  } else {
    category = status;
  }
  
  // Ensure category is a string before calling toLowerCase
  const categoryStr = category != null ? String(category).toLowerCase() : "";
  
  const icons = {
    pending: <Schedule />,
    confirmed: <CheckCircle />,
    processing: <Schedule />,
    shipped: <LocalShipping />,
    dispatch: <LocalShipping />,
    delivered: <CheckCircle />,
    cancelled: <Cancel />,
    returned: <Refresh />,
  };
  return icons[categoryStr] || <Schedule />;
};

const ReturnCancelRequestTable = ({
  data = [],
  searchQuery = "",
  isLoading = false,
  selectedProducts = [],
  onProductSelect,
  onSelectAll,
  isAllSelected = false,
  showCancelRequestActions = false,
  onApproveCancelRequest,
  onRejectCancelRequest,
  updatingCancelRequestOrderId = null,
  showReturnRequestActions = false,
  onApproveReturnRequest,
  onRejectReturnRequest,
  updatingReturnRequestOrderId = null,
  onCheckPaymentStatus,
  checkingPaymentOrderId = null,
}) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());

  const filteredData = data.filter((order) => {
    const q = (searchQuery || "").toLowerCase().trim();
    if (!q) return true;
    const email = (order.user?.email ?? order.user_email ?? "").toLowerCase();
    const orderId = (order.order_id ?? order.id ?? "").toString().toLowerCase();
    const fullName =
      (order.user?.full_name ?? order.user_name ?? order.user?.username ?? "").toLowerCase();
    return email.includes(q) || orderId.includes(q) || fullName.includes(q);
  });

  const toggleRow = (orderId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleSelectAllToggle = (e) => {
    if (onSelectAll) onSelectAll(e.target.checked);
  };

  const isProductSelected = (id) => selectedProducts.includes(id);

  const OrderDetailsRow = ({ order }) => {
    const statusDisplay = getStatusDisplay(order);
    const paymentDisplay = getPaymentStatusDisplay(order);

    return (
      <ExpandableRow>
        <StyledTableCell colSpan={9} sx={{ paddingY: 0, paddingX: 0 }}>
          <Collapse in={expandedRows.has(order.id)} timeout="auto" unmountOnExit>
            <Box sx={{ width: "100%", padding: 2 }}>
              <Grid container spacing={2.5} alignItems="stretch">
                {/* Customer & Address Information */}
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard
                    elevation={2}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      backgroundColor: "background.paper",
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 3,
                      p: 2.5,
                    }}
                  >
                    {/* <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Person sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight={600}>
                        Customer Information
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Name:</strong>{" "}
                      {order.user?.full_name || order.user_name || "—"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {order.user?.email || order.user_email || "—"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Mobile:</strong>{" "}
                      {order.user?.mobile_number || order.mobile_number || "—"}
                    </Typography> */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Person sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight={600}>
                        Customer Information
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Name:</strong>{" "}
                      {order.user?.full_name || order.full_name || "—"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {order.user?.email || order.user_email || "—"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Mobile:</strong>{" "}
                      {order.user?.user_phone|| order.user_phone || "—"}
                    </Typography>

                    {/* Farmer Name - Order on Behalf of */}
                    {(order.farmer_name || order.advance_payment?.order?.farmer_name) && (
                      <Box
                        sx={{
                          mt: 1,
                          mb: 1,
                          p: 1.5,
                          backgroundColor: alpha(theme.palette.info.main, 0.1),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: theme.palette.info.dark, mb: 0.5 }}
                        >
                          Order on Behalf of :{" "}
                          {order.farmer_name || order.advance_payment?.order?.farmer_name}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Delivery Address
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        order.delivery_address ||
                        (order.address_details
                          ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}`
                          : "Address not available")
                      }
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          backgroundColor: alpha(theme.palette.background.default, 0.5),
                          padding: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "help",
                        }}
                      >
                        {order.delivery_address ||
                          (order.address_details
                            ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}`
                            : "Address not available")}
                      </Typography>
                    </Tooltip>

                    {order.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }} fontWeight={600}>
                          Special Instructions:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontStyle: "italic",
                            p: 1.5,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            borderRadius: 1,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          }}
                        >
                          {order.notes}
                        </Typography>
                      </Box>
                    )}

                    {/* Billing Address */}
                    <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Billing Address
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        order.billing_address ||
                        (order.bill_address_details
                          ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}`
                          : "Billing address not available")
                      }
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          backgroundColor: alpha(theme.palette.background.default, 0.5),
                          padding: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "help",
                        }}
                      >
                        {order.billing_address ||
                          (order.bill_address_details
                            ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}`
                            : "Billing address not available")}
                      </Typography>
                    </Tooltip>
                  </DetailCard>
                </Grid>

                {/* Order Status & Payment */}
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard
                    elevation={2}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      p: 2.5,
                      backgroundColor: "background.paper",
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 3,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
                      <LocalShipping
                        sx={{ mr: 1.25, fontSize: "1.25rem", color: theme.palette.success.main }}
                      />
                      <Typography variant="h6" fontWeight={600}>
                        Order Status
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2.5,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          <strong>Order Status:</strong>{" "}
                          <StatusChip
                            label={statusDisplay.name || "—"}
                            status={
                              statusDisplay.category?.toLowerCase() ||
                              statusDisplay.name?.toLowerCase()
                            }
                            size="small"
                            icon={getStatusIcon(order.status)}
                          />
                        </Typography>
                      </Box>
                    </Box>

                    {/* Expected Delivery Date (from farmer) */}
                    {order.expected_delivery_date && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2">
                          <strong>Expected Delivery Date (Farmer):</strong>
                        </Typography>
                        <Typography
                          variant="body2"
                          color="primary"
                          fontWeight={600}
                          sx={{ mt: 0.5 }}
                        >
                          {new Date(order.expected_delivery_date).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </Typography>
                      </Box>
                    )}

                    {/* Actual Delivery Date */}
                    {order.delivery_date && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Actual Delivery Date:</strong>{" "}
                          <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                            {(() => {
                              try {
                                const date = new Date(order.delivery_date);
                                return date.toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                });
                              } catch (e) {
                                return order.delivery_date_formatted || order.delivery_date;
                              }
                            })()}
                          </Typography>
                        </Typography>
                      </Box>
                    )}

                    {/* Returned At */}
                    {(statusDisplay.category || statusDisplay.name)
                      .toLowerCase() === "returned" && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Returned At:</strong>{" "}
                          {(() => {
                            const dt = order.returned_at ?? order.status?.returned_at;
                            if (!dt) return "N/A";
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </Box>
                    )}

                    {/* Cancelled At */}
                    {(statusDisplay.category || statusDisplay.name)
                      .toLowerCase() === "cancelled" && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Cancelled At:</strong>{" "}
                          {(() => {
                            const dt = order.cancelled_at ?? order.status?.cancelled_at;
                            if (!dt) return "N/A";
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Payment
                        sx={{ mr: 1.25, fontSize: "1.25rem", color: theme.palette.info.main }}
                      />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Payment Information
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>Payment Status:</strong>
                      <PaymentStatusChip
                        label={paymentDisplay.name || "Pending"}
                        status={(paymentDisplay.category || paymentDisplay.name || "pending")?.toLowerCase()}
                        size="small"
                        sx={{ ml: 1.25 }}
                      />
                    </Typography>
                    {((order.reason ?? order.status?.reason) != null &&
                      String(order.reason ?? order.status?.reason ?? "").trim() !== "") && (
                      <Typography variant="body2" sx={{ mb: 1.25, mt: 1 }}>
                        <strong>Reason:</strong> {order.reason ?? order.status?.reason ?? ""}
                      </Typography>
                    )}

                    {(paymentDisplay.category || paymentDisplay.name)
                      .toLowerCase() === "refunded" && (
                      <>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded Amount:</strong>{" "}
                          {order.refunded_amount ?? order.payment_status?.refunded_amount ?? "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded At:</strong>{" "}
                          {(() => {
                            const dt = order.refunded_at ?? order.payment_status?.refunded_at;
                            if (!dt) return "N/A";
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                        {onCheckPaymentStatus && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCheckPaymentStatus(order);
                            }}
                            disabled={checkingPaymentOrderId === (order.order_id ?? order.id)}
                            sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}
                          >
                            {checkingPaymentOrderId === (order.order_id ?? order.id) ? (
                              <CircularProgress size={18} sx={{ mr: 0.5 }} />
                            ) : null}
                            Check Payment Status
                          </Button>
                        )}
                      </>
                    )}
                  </DetailCard>
                </Grid>

                {/* Full Payment & Additional Details */}
                {order.advance_payment && (
                  <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                    <DetailCard
                      elevation={2}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "background.paper",
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        borderRadius: 3,
                        p: 2.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
                        <Typography variant="h6" fontWeight={600}>
                          {order.advance_payment.is_full_payment
                            ? "Full Payment"
                            : "Advance Payment"}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>
                          {order.advance_payment.is_full_payment
                            ? "Total Amount:"
                            : "Advance Amount:"}
                        </strong>{" "}
                        {formatCurrency(order.advance_payment.advance_amount)}
                      </Typography>

                      {!order.advance_payment.is_full_payment && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Remaining Amount:</strong>{" "}
                          {formatCurrency(order.advance_payment.remaining_amount)}
                        </Typography>
                      )}

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Method:</strong>{" "}
                        {order.advance_payment.method?.name || order.payment_method?.method || "N/A"}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      {order.advance_payment.fields && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            Additional Details
                          </Typography>

                          {order.advance_payment.fields.cash_receipt_number && (
                            <Typography variant="body2">
                              <strong>Cash Receipt No:</strong>{" "}
                              {order.advance_payment.fields.cash_receipt_number}
                            </Typography>
                          )}

                          {order.advance_payment.fields.receiver_name && (
                            <Typography variant="body2">
                              <strong>Receiver Name:</strong>{" "}
                              {order.advance_payment.fields.receiver_name}
                            </Typography>
                          )}

                          {order.advance_payment.fields.transaction_id && (
                            <Typography variant="body2">
                              <strong>Transaction ID:</strong>{" "}
                              {order.advance_payment.fields.transaction_id}
                            </Typography>
                          )}

                          {order.advance_payment.fields.upi_id_used && (
                            <Typography variant="body2">
                              <strong>UPI ID:</strong> {order.advance_payment.fields.upi_id_used}
                            </Typography>
                          )}

                          {order.advance_payment.fields.transaction_reference_no && (
                            <Typography variant="body2">
                              <strong>Reference No:</strong>{" "}
                              {order.advance_payment.fields.transaction_reference_no}
                            </Typography>
                          )}

                          {order.advance_payment.fields.sender_bank_name && (
                            <Typography variant="body2">
                              <strong>Sender Bank:</strong>{" "}
                              {order.advance_payment.fields.sender_bank_name}
                            </Typography>
                          )}

                          {order.advance_payment.fields.bank_name_branch && (
                            <Typography variant="body2">
                              <strong>Bank Branch:</strong>{" "}
                              {order.advance_payment.fields.bank_name_branch}
                            </Typography>
                          )}

                          {order.advance_payment.fields.cheque_number && (
                            <Typography variant="body2">
                              <strong>Cheque No:</strong>{" "}
                              {order.advance_payment.fields.cheque_number}
                            </Typography>
                          )}

                          {order.advance_payment.fields.cheque_date && (
                            <Typography variant="body2">
                              <strong>Cheque Date:</strong>{" "}
                              {order.advance_payment.fields.cheque_date}
                            </Typography>
                          )}

                          {order.advance_payment.fields.cheque_amount && (
                            <Typography variant="body2">
                              <strong>Cheque Amount:</strong>{" "}
                              {formatCurrency(order.advance_payment.fields.cheque_amount)}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Fallback: Show payment info from order if advance_payment doesn't exist */}
                      {!order.advance_payment && (
                        <>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Total Amount:</strong> {formatCurrency(order.total_amount)}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Method:</strong>{" "}
                            {order.payment_method?.method || order.payment_method_name || "N/A"}
                          </Typography>
                          {order.transaction_id && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="h6" fontWeight={600} gutterBottom>
                                Additional Details
                              </Typography>
                              <Typography variant="body2">
                                <strong>Transaction ID:</strong> {order.transaction_id}
                              </Typography>
                            </>
                          )}
                        </>
                      )}
                    </DetailCard>
                  </Grid>
                )}

                {/* Fallback: Show payment card even if no advance_payment */}
                {!order.advance_payment && (
                  <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                    <DetailCard
                      elevation={2}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "background.paper",
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        borderRadius: 3,
                        p: 2.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
                        <Typography variant="h6" fontWeight={600}>
                          Full Payment
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Total Amount:</strong> {formatCurrency(order.total_amount)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Method:</strong>{" "}
                        {order.payment_method?.method || order.payment_method_name || "N/A"}
                      </Typography>
                      {order.transaction_id && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            Additional Details
                          </Typography>
                          <Typography variant="body2">
                            <strong>Transaction ID:</strong> {order.transaction_id}
                          </Typography>
                        </>
                      )}
                    </DetailCard>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </StyledTableCell>
      </ExpandableRow>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 280,
          p: 4,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={(t) => ({
        maxHeight: 600,
        overflowX: "auto",
        borderRadius: 2,
        boxShadow: "none",
        border: `1px solid ${alpha(t.palette.divider, 0.1)}`,
      })}
    >
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            <StyledTableCell className="header" sx={{ width: 50,  backgroundColor: theme.palette.grey[100] ,}}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={selectedProducts.length > 0 && !isAllSelected}
                onChange={handleSelectAllToggle}
                color="primary"
                sx ={{paddingLeft: 3,}}
              />
            </StyledTableCell>
            <StyledTableCell className="header" sx={{ width: 50, backgroundColor: theme.palette.grey[100] }}></StyledTableCell>
            <StyledTableCell className="header" sx={{ backgroundColor: theme.palette.grey[100] }}>Order ID</StyledTableCell>
            <StyledTableCell className="header" sx={{ backgroundColor: theme.palette.grey[100] }}>Customer</StyledTableCell>
            <StyledTableCell className="header" sx={{ backgroundColor: theme.palette.grey[100] }}>User Email</StyledTableCell>
            <StyledTableCell className="header" sx={{ backgroundColor: theme.palette.grey[100] }}>Status</StyledTableCell>
            <StyledTableCell className="header" sx={{ backgroundColor: theme.palette.grey[100] }}>Payment Status</StyledTableCell>
            <StyledTableCell className="header" sx={{ backgroundColor: theme.palette.grey[100] }}>Total Amount</StyledTableCell>
            <StyledTableCell className="header" sx={{ backgroundColor: theme.palette.grey[100] }}>Order Date</StyledTableCell>
            <StyledTableCell
              className="header"
              sx={{
                position: "sticky",
                right: 0,
                zIndex: 5,
                backgroundColor: theme.palette.grey[100],
              }}
            >
              Actions
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} sx={{ border: "none",pr: 100 ,}} >
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <Typography color="text.secondary">No orders found</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((order) => {
              const orderId = order.order_id ?? order.id;
              const isSelected = isProductSelected(order.id);
              const statusDisplay = getStatusDisplay(order);
              const paymentDisplay = getPaymentStatusDisplay(order);
              const isExpanded = expandedRows.has(order.id);
              const cancelAction = getRequestAction(order, "cancel_request_action");
              const returnAction = getRequestAction(order, "return_request_action");

              return (
                <React.Fragment key={order.id}>
                  <StyledTableRow hover selected ={isSelected}>
                    <StyledTableCell>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) =>
                          onProductSelect && onProductSelect(order.id, e.target.checked)
                        }
                        color="primary"
                        size="small"
                        sx={{paddingLeft: 3,}}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(order.id)}
                          sx={{
                            transition: "transform 0.2s",
                            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                          }}
                        >
                          <KeyboardArrowRightIcon />
                        </IconButton>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <Typography variant="body2" fontWeight={500}>
                        {orderId ?? "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <Typography variant="body2">
                        {order.user?.full_name ?? order.user_name ?? order.user?.username ?? "—"}
                      </Typography>
                      {(order.user?.mobile_number ?? order.mobile_number) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {order.user?.mobile_number ?? order.mobile_number}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <Typography variant="body2">
                        {order.user?.email ?? order.user_email ?? "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <StatusChip
                        label={statusDisplay.name || "—"}
                        status={
                          statusDisplay.category?.toLowerCase() ||
                          statusDisplay.name?.toLowerCase()
                        }
                        size="small"
                      />
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <PaymentStatusChip
                        label={paymentDisplay.name || "—"}
                        status={(paymentDisplay.category || paymentDisplay.name || "pending")?.toLowerCase()}
                        size="small"
                      />
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(order.total_amount)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <Typography variant="body2">
                        {order.order_date_formatted ?? order.created_at ?? "—"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 1,
                        backgroundColor: theme.palette.background.paper,
                       
                      }}
                    >
                      <Box display="flex" gap={1} sx={{ pr: 3}}>
                        {showCancelRequestActions &&
                          onApproveCancelRequest &&
                          onRejectCancelRequest && (() => {
                            const isPending = cancelAction === null;
                            const isApproved = cancelAction === "approved";
                            const isRejected = cancelAction === "rejected";
                            return (
                              <>
                                {/* Pending: show both buttons enabled. Approved: show only Accepted (disabled). Rejected/other: show only Rejected (disabled). */}
                                {(isPending || isApproved) && (
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    disabled={isApproved || updatingCancelRequestOrderId === orderId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isApproved) onApproveCancelRequest(order);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                  >
                                    {updatingCancelRequestOrderId === orderId ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      "Accepted"
                                    )}
                                  </Button>
                                )}
                                {(isPending || isRejected) && (
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    disabled={isRejected || updatingCancelRequestOrderId === orderId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isRejected) onRejectCancelRequest(order);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 600, }}
                                    
                                  >
                                    Rejected
                                  </Button>
                                )}
                              </>
                            );
                          })()}
                        {showReturnRequestActions &&
                          onApproveReturnRequest &&
                          onRejectReturnRequest && (() => {
                            const isPending = returnAction === null;
                            const isApproved = returnAction === "approved";
                            const isRejected = returnAction === "rejected";
                            return (
                              <>
                                {(isPending || isApproved) && (
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    disabled={isApproved || updatingReturnRequestOrderId === orderId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isApproved) onApproveReturnRequest(order);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                  >
                                    {updatingReturnRequestOrderId === orderId ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      "Accepted"
                                    )}
                                  </Button>
                                )}
                                {(isPending || isRejected) && (
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    disabled={isRejected || updatingReturnRequestOrderId === orderId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isRejected) onRejectReturnRequest(order);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                  >
                                    Rejected
                                  </Button>
                                )}
                              </>
                            );
                          })()}
                        {((paymentDisplay.category || paymentDisplay.name) || "").toLowerCase() === "refunded" &&
                          onCheckPaymentStatus && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              disabled={checkingPaymentOrderId === orderId}
                              onClick={(e) => {
                                e.stopPropagation();
                                onCheckPaymentStatus(order);
                              }}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              {checkingPaymentOrderId === orderId ? (
                                <CircularProgress size={18} />
                              ) : (
                                "Check Payment Status"
                              )}
                            </Button>
                          )}
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                  <OrderDetailsRow order={order} />
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReturnCancelRequestTable;
